import { EntityId, EntityState, createEntityAdapter } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/query/react';
import capitalize from 'lodash.capitalize';

import { entityFilter, EntityCompareFunc } from '@/utils/entityFilter';
import { humanizeTier } from '@/utils/humanizeTier';

import { api } from '../api';

import {
  PassportEntity,
  AnnualPassResponse,
  GetMemberRewardInfoResponse,
  PassTier,
  PassportStatus,
  GetAllRewardsForActivePassesApiArg,
  GetAllRewardsForActivePassesApiResponse,
  MemberRewardInfoEntity,
  GetAnnualPassesApiResponse,
  GetAnnualPassesApiArg,
  GetRewardsApiResponse,
  GetRewardsApiArg,
} from './types';

const activeCompareFunc: EntityCompareFunc<PassportEntity> = ([, data]) =>
  (data?.status === PassportStatus.ACTIVE || data?.status === PassportStatus.EXPIRING_SOON) &&
  !data.isOnHold;

const annualPassesAdapter = createEntityAdapter<PassportEntity, EntityId>({
  selectId: (pass) => pass.passmemberBarcode ?? '',
});
const initialAnnualPassesState = annualPassesAdapter.getInitialState();

const rewardsAdapter = createEntityAdapter<MemberRewardInfoEntity, EntityId>({
  selectId: (reward) => reward.id ?? '',
});
const initialRewardsState = rewardsAdapter.getInitialState();

export const accountApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /** Gets all annual passes for user. */
    getAnnualPasses: builder.query<GetAnnualPassesApiResponse, GetAnnualPassesApiArg>({
      query: () => '/account/annual-passes',
      providesTags: ['Passes'],
      transformResponse: (response: AnnualPassResponse) => {
        const { annualPasses } = response;

        const loadedAnnualPasses = annualPasses.map(
          ({
            passmemberFirstName,
            passmemberLastName,
            expirationDate,
            tier,
            ...rest
          }): PassportEntity => {
            const firstName = capitalize(passmemberFirstName ?? '');
            const lastName = capitalize(passmemberLastName ?? '');
            const fullName = `${firstName} ${lastName}`;
            const sanitizedTier = tier ?? PassTier.DEFAULT;

            return {
              passmemberFirstName: firstName,
              passmemberLastName: lastName,
              passmemberFullName: fullName,
              tier: sanitizedTier,
              tierHumanized: humanizeTier(sanitizedTier),
              expirationDate: expirationDate?.split(' ')[0], // Fix for Safari
              ...rest,
            };
          }
        );

        return annualPassesAdapter.setAll(initialAnnualPassesState, loadedAnnualPasses);
      },
    }),

    /** Gets rewards for single pass */
    getRewards: builder.query<GetRewardsApiResponse, GetRewardsApiArg>({
      query: (barcode: string) => `/account/annual-passes/rewards/${barcode}`,
      providesTags: ['Rewards'],
      transformResponse: (response: GetMemberRewardInfoResponse, meta, arg) => {
        const rewards = (response.memberRewardInfo ?? [])
          .filter((reward) => !!reward.sellingGroupSitecoreId)
          .map((reward) => ({
            ...reward,
            id: crypto.randomUUID(),
            associatedBarcode: arg,
            filterTags: reward.filterTags || [],
          }));

        return rewardsAdapter.setAll(initialRewardsState, rewards);
      },
    }),

    /** Gets rewards across all ACTIVE passes for user */
    getAllRewardsForActivePasses: builder.query<
      GetAllRewardsForActivePassesApiResponse,
      GetAllRewardsForActivePassesApiArg
    >({
      providesTags: ['Rewards', 'Passes'],
      async queryFn(_arg, queryApi) {
        try {
          const annualPassesResult = (await queryApi.dispatch(
            accountApi.endpoints.getAnnualPasses.initiate()
          )) as TypedUseQueryHookResult<EntityState<PassportEntity, EntityId>, unknown, any>;

          if (annualPassesResult.isError) {
            return { error: annualPassesResult.error as FetchBaseQueryError };
          }

          const { data } = annualPassesResult;

          const filterFunc = entityFilter<PassportEntity>(activeCompareFunc);
          const { entities } = filterFunc(data?.entities);
          const barcodes = Object.values(entities)
            .map((entity) => entity.passmemberBarcode)
            .filter((barcode) => !!barcode);

          const rewardsRequests = (barcodes as string[]).map((barcode) =>
            queryApi.dispatch(accountApi.endpoints.getRewards.initiate(barcode))
          );

          const rewardsResponses = (await Promise.all(rewardsRequests)) as TypedUseQueryHookResult<
            EntityState<MemberRewardInfoEntity, EntityId>,
            unknown,
            any
          >[];

          const allRewards = rewardsResponses.reduce((acc, curr) => {
            const rewards = Object.values(
              curr.data?.entities as Record<string, MemberRewardInfoEntity>
            );
            return [...acc, ...rewards];
          }, [] as MemberRewardInfoEntity[]);

          return { data: allRewards };
        } catch (error) {
          return { error: error as FetchBaseQueryError };
        }
      },
    }),
  }),
});

export const {
  useGetAnnualPassesQuery,
  useGetRewardsQuery,
  useGetAllRewardsForActivePassesQuery,
  usePrefetch: useAccountPrefetch,
} = accountApi;
